import { AnalyticsClient } from '../../analytics/client';
import { BotController, getBotDefinition, listBotDefinitions, type BotStrategySnapshot } from '../../bot/bot';
import { ADAPTIVE_BOT_ID } from '../../bot/implementations/adaptive/adaptive-bot';
import { extractPlayerMatchFeatures } from '../../bot/learning/feature-extractor';
import { createEmptyPlayerLearningModel, dominantPlayerStyle, hashPlayerModel, type PlayerLearningModel } from '../../bot/learning/types';
import { trainPlayerLearningModel } from '../../bot/learning/trainer';
import type { ContentSnapshot, PlayerId } from '../../engine/types';
import type { BattleEngine } from '../../engine/engine';
import { compareStableText } from '../../engine/stable-order';
import type { I18nPort } from '../../i18n/i18n-service';
import type { MessageKey } from '../../i18n/message-keys';
import { CARD_IMAGES } from '../../renderer/assets';
import { BOT_PREFERENCES_STORAGE_KEY, parseBotPreferences, saveBotPreferences, type BotPreferences } from '../../storage/bot-preferences-store';
import { DECK_STORAGE_KEY, parseSavedDeckSelection, saveDeckSelection, type DeckSelection } from '../../storage/deck-store';
import { clearPlayerLearningModel, loadPlayerLearningModel, savePlayerLearningModel } from '../../storage/player-model-store';
import { normalizePlayerName, parsePlayerProfile, PLAYER_PROFILE_STORAGE_KEY, savePlayerProfile, type PlayerProfile } from '../../storage/player-profile-store';
import { requireElement } from '../dom';
import type { ToastController } from './toast-controller';

export interface PreparedBotOpponent {
  readonly strategy: BotStrategySnapshot;
  readonly definition: ReturnType<typeof getBotDefinition>;
  readonly selection: ReturnType<ReturnType<typeof getBotDefinition>['selectDeck']>;
}

export class LobbyController {
  private readonly playerNameInput = requireElement<HTMLInputElement>('#playerNameInput');
  private readonly playerNameLabel = requireElement<HTMLElement>('#playerNameLabel');
  private readonly playerDeckTab = requireElement<HTMLButtonElement>('#playerDeckTab');
  private readonly botDeckTab = requireElement<HTMLButtonElement>('#botDeckTab');
  private readonly deckTitle = requireElement<HTMLElement>('#deckTitle');
  private readonly deckAverage = requireElement<HTMLElement>('#deckAverage');
  private readonly deckSlots = requireElement<HTMLElement>('#deckSlots');
  private readonly cardCatalog = requireElement<HTMLElement>('#cardCatalog');
  private readonly botType = requireElement<HTMLSelectElement>('#botTypeSelect');
  private readonly botLearningStatus = requireElement<HTMLElement>('#botLearningStatus');
  private readonly botStrategySummary = requireElement<HTMLElement>('#botStrategySummary');
  private readonly resetBotLearning = requireElement<HTMLButtonElement>('#resetBotLearningButton');
  private readonly botDefinitions = listBotDefinitions();
  private readonly defaultDecks: Readonly<Record<PlayerId, readonly import('../../engine/types').CardId[]>>;
  private deckSelection: DeckSelection;
  private deckEditorOwner: PlayerId = 'player';
  private selectedDeckSlot = 0;
  private botPreferences: BotPreferences;
  private playerProfile: PlayerProfile;
  private playerLearningModel: PlayerLearningModel;
  private lastStrategy: BotStrategySnapshot | null = null;
  private readonly parsedDecks;
  private readonly parsedBotPreferences;

  constructor(
    private readonly content: ContentSnapshot,
    defaultDecks: Readonly<Record<PlayerId, readonly import('../../engine/types').CardId[]>>,
    private readonly i18n: I18nPort,
    private readonly analytics: AnalyticsClient,
    private readonly toast: ToastController,
  ) {
    this.defaultDecks = defaultDecks;
    this.parsedDecks = parseSavedDeckSelection(localStorage.getItem(DECK_STORAGE_KEY), new Set(content.cards.keys()), defaultDecks);
    this.deckSelection = this.parsedDecks.selection;
    this.parsedBotPreferences = parseBotPreferences(localStorage.getItem(BOT_PREFERENCES_STORAGE_KEY), new Set(this.botDefinitions.map((definition) => definition.id)), ADAPTIVE_BOT_ID);
    this.botPreferences = this.parsedBotPreferences.preferences;
    this.playerProfile = parsePlayerProfile(localStorage.getItem(PLAYER_PROFILE_STORAGE_KEY));
    this.playerLearningModel = createEmptyPlayerLearningModel(content.version);
    this.bindEvents();
    this.i18n.subscribe(() => this.render());
  }

  get decks(): Readonly<Record<PlayerId, readonly import('../../engine/types').CardId[]>> {
    return { player: this.deckSelection.player, bot: this.deckSelection.bot };
  }

  get displayName(): string {
    return this.playerProfile.displayName ?? this.i18n.t('profile.placeholder');
  }

  get learningModel(): PlayerLearningModel { return this.playerLearningModel; }

  async boot(): Promise<void> {
    this.syncPlayerName();
    this.renderDeckBuilder();
    try {
      const loaded = await loadPlayerLearningModel(this.content.version);
      this.playerLearningModel = loaded.model;
      if (loaded.repaired) this.toast.show(this.i18n.t('bot.modelRepaired'));
    } catch (error) {
      this.playerLearningModel = createEmptyPlayerLearningModel(this.content.version);
      this.toast.show(this.i18n.t('bot.learningUnavailable', { error: String(error) }));
    }
    this.renderBotConfiguration();
    if (this.parsedDecks.repaired) this.toast.show(this.i18n.t('toast.deckRepaired'));
    if (this.parsedBotPreferences.repaired) this.toast.show(this.i18n.t('bot.preferenceRepaired'));
  }

  commitPlayerName(notifyOnFailure = true): void {
    this.playerProfile = { schemaVersion: 2, displayName: normalizePlayerName(this.playerNameInput.value) };
    this.syncPlayerName();
    if (!savePlayerProfile(localStorage, this.playerProfile) && notifyOnFailure) this.toast.show(this.i18n.t('toast.playerSaveFailed'));
  }

  prepareBot(seed: number): PreparedBotOpponent {
    const definition = getBotDefinition(this.botPreferences.botId);
    const selection = definition.selectDeck({ seed, content: this.content, manualDeck: this.deckSelection.bot, model: this.playerLearningModel });
    const strategy: BotStrategySnapshot = Object.freeze({
      schemaVersion: 1,
      botId: definition.id,
      botVersion: definition.version,
      deckPolicyId: definition.deckPolicyId,
      deckPolicyVersion: definition.deckPolicyVersion,
      modelVersion: definition.supportsLearning ? this.playerLearningModel.modelVersion : null,
      modelHash: definition.supportsLearning ? hashPlayerModel(this.playerLearningModel) : 'none',
      confidence: definition.supportsLearning ? this.playerLearningModel.confidence : 0,
      sourceMatches: definition.supportsLearning ? this.playerLearningModel.matchCount : 0,
      style: definition.supportsLearning ? { ...this.playerLearningModel.style } : { ...createEmptyPlayerLearningModel(this.content.version).style },
      selectedDeck: [...selection.deck],
      deckScore: selection.score,
      rationale: [...selection.rationale],
    });
    this.lastStrategy = strategy;
    this.renderBotConfiguration();
    return { strategy, definition, selection };
  }

  createBotController(seed: number, engine: BattleEngine, prepared: PreparedBotOpponent): BotController {
    return new BotController(prepared.definition, { seed, content: this.content, journal: engine.journal, playerId: 'bot', strategy: prepared.strategy });
  }

  async learnFrom(engine: BattleEngine, strategy: BotStrategySnapshot | null): Promise<void> {
    if (!strategy || !getBotDefinition(strategy.botId).supportsLearning) return;
    try {
      const features = extractPlayerMatchFeatures(engine.journal.matchId, engine.replay, this.content);
      engine.journal.record({ tick: engine.currentTick, phase: 'bot', source: 'player-style-extractor', code: 'PLAYER_MODEL_EXTRACTED', payload: { matchId: features.matchId, plays: features.plays, averageCostMilli: features.averageCostMilli, style: features.style } });
      const training = trainPlayerLearningModel(this.playerLearningModel, features, this.content);
      if (!training.updated) {
        engine.journal.record({ tick: engine.currentTick, phase: 'bot', source: 'player-style-trainer', code: 'PLAYER_MODEL_SKIPPED', payload: { reason: 'duplicate-match', matchId: features.matchId } });
        return;
      }
      await savePlayerLearningModel(training.model);
      this.playerLearningModel = training.model;
      engine.journal.record({ tick: engine.currentTick, phase: 'bot', source: 'player-style-trainer', code: 'PLAYER_MODEL_UPDATED', payload: { modelVersion: this.playerLearningModel.modelVersion, revision: this.playerLearningModel.revision, matchCount: this.playerLearningModel.matchCount, confidence: this.playerLearningModel.confidence, modelHash: hashPlayerModel(this.playerLearningModel), style: this.playerLearningModel.style } });
      this.renderBotConfiguration();
    } catch (error) {
      engine.journal.record({ tick: engine.currentTick, phase: 'bot', source: 'player-style-trainer', level: 'warn', code: 'PLAYER_MODEL_SKIPPED', payload: { reason: 'training-failed', error: String(error) } });
    }
  }

  render(): void {
    this.syncPlayerName();
    this.renderDeckBuilder();
    this.renderBotConfiguration();
  }

  private bindEvents(): void {
    this.playerNameInput.addEventListener('input', () => { this.playerNameLabel.textContent = normalizePlayerName(this.playerNameInput.value) ?? this.i18n.t('profile.placeholder'); });
    this.playerNameInput.addEventListener('blur', () => this.commitPlayerName());
    this.playerDeckTab.addEventListener('click', () => { this.deckEditorOwner = 'player'; this.selectedDeckSlot = 0; this.renderDeckBuilder(); });
    this.botDeckTab.addEventListener('click', () => { this.deckEditorOwner = 'bot'; this.selectedDeckSlot = 0; this.renderDeckBuilder(); });
    this.botType.addEventListener('change', () => {
      this.botPreferences = { schemaVersion: 1, botId: this.botType.value };
      if (!saveBotPreferences(localStorage, this.botPreferences)) this.toast.show(this.i18n.t('toast.botSaveFailed'));
      this.renderBotConfiguration();
    });
    this.resetBotLearning.addEventListener('click', () => { void this.resetLearning(); });
    requireElement<HTMLButtonElement>('#resetDeckButton').addEventListener('click', () => {
      this.deckSelection = { ...this.deckSelection, [this.deckEditorOwner]: [...this.defaultDecks[this.deckEditorOwner]] };
      this.selectedDeckSlot = 0;
      this.persistDeckSelection();
      this.renderDeckBuilder();
    });
  }

  private async resetLearning(): Promise<void> {
    if (this.playerLearningModel.matchCount === 0) return;
    try {
      await clearPlayerLearningModel();
      this.playerLearningModel = createEmptyPlayerLearningModel(this.content.version);
      this.renderBotConfiguration();
      this.toast.show(this.i18n.t('bot.learningReset'));
    } catch (error) {
      this.toast.show(this.i18n.t('bot.learningResetFailed', { error: String(error) }));
    }
  }

  private persistDeckSelection(): void {
    if (!saveDeckSelection(localStorage, this.deckSelection)) this.toast.show(this.i18n.t('toast.deckSaveFailed'));
    this.analytics.event('deck_saved', { owner: this.deckEditorOwner });
  }

  private syncPlayerName(): void {
    this.playerNameInput.value = this.playerProfile.displayName ?? '';
    this.playerNameLabel.textContent = this.displayName;
    this.playerNameLabel.title = this.displayName;
  }

  private renderBotConfiguration(): void {
    this.botType.replaceChildren();
    for (const definition of this.botDefinitions) {
      const option = document.createElement('option');
      option.value = definition.id;
      option.textContent = `${this.i18n.text(definition.nameKey)} · ${definition.version}`;
      option.selected = definition.id === this.botPreferences.botId;
      this.botType.append(option);
    }
    const definition = getBotDefinition(this.botPreferences.botId);
    const styleKey = `style.${dominantPlayerStyle(this.playerLearningModel.style)}` as MessageKey;
    const lastDeck = this.lastStrategy?.botId === definition.id ? this.i18n.t('bot.lastDeck', { deck: this.lastStrategy.selectedDeck.join(', ') }) : '';
    this.botLearningStatus.textContent = definition.supportsLearning
      ? this.i18n.t('bot.learning', { count: this.playerLearningModel.matchCount, confidence: Math.round(this.playerLearningModel.confidence / 10) })
      : this.i18n.t('bot.learningOff');
    this.botStrategySummary.textContent = definition.supportsLearning
      ? this.playerLearningModel.matchCount > 0
        ? this.i18n.t('bot.modelCurrent', { style: this.i18n.t(styleKey), hash: hashPlayerModel(this.playerLearningModel), lastDeck })
        : this.i18n.t('bot.modelEmpty')
      : this.i18n.t('bot.classicPolicy', { lastDeck });
    this.resetBotLearning.disabled = this.playerLearningModel.matchCount === 0;
  }

  private renderDeckBuilder(): void {
    const deck = this.deckSelection[this.deckEditorOwner];
    this.playerDeckTab.classList.toggle('active', this.deckEditorOwner === 'player');
    this.botDeckTab.classList.toggle('active', this.deckEditorOwner === 'bot');
    this.deckTitle.textContent = this.i18n.t(this.deckEditorOwner === 'player' ? 'deck.titleMine' : 'deck.titleBot', { count: deck.length });
    const totalCost = deck.reduce((sum, cardId) => sum + (this.content.cards.get(cardId)?.cost ?? 0), 0);
    this.deckAverage.textContent = this.i18n.t('deck.average', { value: (totalCost / deck.length).toFixed(1) });
    this.deckSlots.replaceChildren();
    deck.forEach((cardId, index) => {
      const card = this.content.cards.get(cardId)!;
      const cardName = this.i18n.text(card.nameKey);
      const button = document.createElement('button');
      button.className = `deck-slot${index === this.selectedDeckSlot ? ' selected' : ''}`;
      button.type = 'button';
      button.setAttribute('aria-label', this.i18n.t('deck.slot', { slot: index + 1, card: cardName }));
      const image = document.createElement('img'); image.src = CARD_IMAGES[cardId] ?? ''; image.alt = cardName;
      const number = document.createElement('small'); number.textContent = String(index + 1);
      button.append(image, number);
      button.addEventListener('click', () => { this.selectedDeckSlot = index; this.renderDeckBuilder(); });
      this.deckSlots.append(button);
    });
    this.cardCatalog.replaceChildren();
    for (const card of [...this.content.cards.values()].sort((left, right) => left.cost - right.cost || compareStableText(left.id, right.id))) {
      const existingIndex = deck.indexOf(card.id);
      const cardName = this.i18n.text(card.nameKey);
      const button = document.createElement('button');
      button.className = `catalog-card${existingIndex >= 0 ? ' in-deck' : ''}`;
      button.type = 'button';
      button.setAttribute('aria-label', this.i18n.t('deck.cardAria', { card: cardName, cost: card.cost, selected: existingIndex >= 0 ? this.i18n.t('deck.alreadySelected') : '' }));
      const image = document.createElement('img'); image.src = CARD_IMAGES[card.id] ?? ''; image.alt = cardName;
      const cost = document.createElement('b'); cost.textContent = String(card.cost);
      const name = document.createElement('span'); name.textContent = cardName;
      button.append(image, cost, name);
      button.addEventListener('click', () => {
        const current = this.deckSelection[this.deckEditorOwner];
        const alreadySelected = current.indexOf(card.id);
        if (alreadySelected >= 0) this.selectedDeckSlot = alreadySelected;
        else {
          const next = [...current]; next[this.selectedDeckSlot] = card.id;
          this.deckSelection = { ...this.deckSelection, [this.deckEditorOwner]: next };
          this.selectedDeckSlot = (this.selectedDeckSlot + 1) % next.length;
          this.persistDeckSelection();
        }
        this.renderDeckBuilder();
      });
      this.cardCatalog.append(button);
    }
  }
}
